import { Button, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { ruRU } from '@mui/x-date-pickers/locales';
import { Dayjs } from 'dayjs';

import {
    DATE_PICKER_SLOT_PROPS,
    DATE_PICKER_TO_SLOT_PROPS,
    detectPreset,
    IntervalState,
    PRESETS,
} from '../time-interval-utils';

export type { IntervalState };
export { defaultIntervalState } from '../time-interval-utils';

//=================================================================================================
interface Props {
    state: IntervalState;
    onChange: (state: IntervalState) => void;
}

export default function TimeIntervalSelector({ state: { from, to }, onChange }: Props) {
    const activePreset = detectPreset(from, to);

    const applyPreset = (idx: number) => {
        const days = PRESETS[idx].days - 1;
        onChange({ from: to.subtract(days, 'day').startOf('day'), to: to.endOf('day') });
    };

    const handleFromChange = (value: Dayjs | null) => {
        if (!value) return;
        onChange({ from: value.startOf('day'), to });
    };

    const handleToChange = (value: Dayjs | null) => {
        if (!value) return;
        onChange({ from, to: value.endOf('day') });
    };

    return (
        <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="ru"
            localeText={ruRU.components.MuiLocalizationProvider.defaultProps.localeText}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ m: 1 }}>
                <Stack
                    direction="row"
                    sx={{
                        border: 0.666,
                        borderColor: 'divider',
                        borderRadius: 1,
                        height: 32,
                        px: 0.5,
                        gap: 0.5,
                        alignItems: 'center',
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: 'text.primary' },
                        '&:focus-within': { borderColor: 'primary.main' },
                    }}
                >
                    {PRESETS.map((preset, idx) => (
                        <Button
                            key={preset.label}
                            variant={activePreset === idx ? 'contained' : 'text'}
                            onClick={() => applyPreset(idx)}
                            sx={{
                                minWidth: 0,
                                px: 1,
                                py: 0.1,
                                textTransform: 'none',
                                fontWeight: 400,
                                lineHeight: 1.5,
                            }}
                        >
                            {preset.label}
                        </Button>
                    ))}
                </Stack>

                <Typography variant="body2" color="text.primary" sx={{ pl: 2 }}>
                    от:
                </Typography>

                <DatePicker
                    value={from}
                    onChange={handleFromChange}
                    maxDate={to}
                    format="DD.MM.YYYY"
                    slotProps={DATE_PICKER_SLOT_PROPS}
                />

                <Typography variant="body2" color="text.primary" sx={{ pl: 2 }}>
                    до:
                </Typography>

                <DatePicker
                    value={to}
                    onChange={handleToChange}
                    minDate={from}
                    format="DD.MM.YYYY"
                    slotProps={DATE_PICKER_TO_SLOT_PROPS}
                />
            </Stack>
        </LocalizationProvider>
    );
}
